import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClientService } from '../../../../core/services/http-client.service';
import { MATRIX_FAVORITES } from '../../../constants/api-routes.constants';
import {
  type IMatrixFavorite,
  type IMatrixFavoriteCreate,
  type IMatrixFavoriteColumnFilter,
} from '../../../models/favorite.model';
import {
  createFavoriteDto,
  mapFavoriteFiltersToFilterOptions,
  mapFavoriteFiltersToFiltersByTab,
  mapFavoritesToOptions,
  syncFavoriteFilters,
} from '../../../utils/favorite-functions.utils';
import { type IFilterOptions } from '../components/favorites-filters-dropdown/models/filter-options.model';
import type { IFilterConfigValue } from '../../../utils/favorite-functions.utils';
import type { FavoritesClient } from '../../../../core/tokens/favorites-client.token';

export interface LoadFavoriteResult {
  favorite: IMatrixFavorite;
  allFavorites: IMatrixFavorite[];
}

@Injectable({
  providedIn: 'root',
})
export class FavoritesService implements FavoritesClient {
  private readonly http = inject(HttpClientService);

  private readonly apiPath = MATRIX_FAVORITES.BASE;

  constructor() {}

  fetchFavoritesBYDashboardAndUsername(dashboardId: number): Observable<IMatrixFavorite[]> {
    return this.http.get<IMatrixFavorite[]>(MATRIX_FAVORITES.BY_DASHBOARD(dashboardId));
  }

  fetchFavoriteById(favoriteId: number): Observable<IMatrixFavorite> {
    return this.http.get<IMatrixFavorite>(`${this.apiPath}/${favoriteId}`);
  }

  fetchFavoritesByUsername(): Observable<IMatrixFavorite[]> {
    return this.http.get<IMatrixFavorite[]>(MATRIX_FAVORITES.BY_USERNAME());
  }

  createFavorite(favorite: IMatrixFavoriteCreate): Observable<IMatrixFavorite> {
    return this.http.post<IMatrixFavorite>(this.apiPath, favorite);
  }

  updateFavorite(favoriteId: number, payload: IMatrixFavorite): Observable<IMatrixFavorite> {
    return this.http.put<IMatrixFavorite>(`${this.apiPath}/${favoriteId}`, payload);
  }

  deleteFavorite(favoriteId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiPath}/${favoriteId}`);
  }

  /**
   * Loads all favorites for a user and transforms them to options format.
   * On 401 (session expired), returns empty options so the auth interceptor can handle logout without triggering component error toasts.
   */
  loadFavoritesAsOptions(): Observable<ReturnType<typeof mapFavoritesToOptions>> {
    return this.fetchFavoritesByUsername().pipe(
      map((favorites) => mapFavoritesToOptions(favorites)),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) return of([]);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Loads favorites for a user filtered by dashboard and transforms them to options format.
   * On 401 (session expired), returns empty options so the auth interceptor can handle logout without triggering component error toasts.
   */
  loadFavoritesAsOptionsByDashboard(
    dashboardId: number,
  ): Observable<ReturnType<typeof mapFavoritesToOptions>> {
    return this.fetchFavoritesBYDashboardAndUsername(dashboardId).pipe(
      map((favorites) => mapFavoritesToOptions(favorites)),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) return of([]);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Loads a favorite by ID and returns filters (legacy single-tab), filtersByTab (grouped by tabIndex), and columnFilters (table column filters).
   */
  loadFavoriteWithFilters(favoriteId: number): Observable<{
    favorite: IMatrixFavorite;
    filters: IFilterOptions;
    filtersByTab: IFilterOptions[];
    columnFilters: IMatrixFavoriteColumnFilter[];
  }> {
    return this.fetchFavoriteById(favoriteId).pipe(
      map((favorite) => ({
        favorite,
        filters: mapFavoriteFiltersToFilterOptions(favorite),
        filtersByTab: mapFavoriteFiltersToFiltersByTab(favorite.matrixFavoriteFilters || []),
        columnFilters: favorite.matrixFavoriteColumnFilters ?? [],
      })),
    );
  }

  /**
   * Loads a favorite and all favorites for a user (useful after creating/updating)
   */
  loadFavoriteAndAllFavorites(
    favoriteId: number,
    username: string,
  ): Observable<LoadFavoriteResult> {
    return forkJoin({
      favorite: this.fetchFavoriteById(favoriteId),
      allFavorites: this.fetchFavoritesByUsername(),
    });
  }

  /**
   * Creates a favorite. For multi-tab pass tabs, flat formValue, and filtersConfig so every tab × every field is saved.
   * Pass columnFilters to persist current table column filter state.
   */
  createFavoriteFromFilters(
    filters: Record<string, unknown>,
    dashboardId: number,
    favoriteName: string,
    organizationId: string,
    tabs?: { id: string }[],
    filtersConfig?: readonly IFilterConfigValue[],
    columnFilters?: IMatrixFavoriteColumnFilter[],
  ): Observable<IMatrixFavorite> {
    const payload = tabs?.length
      ? createFavoriteDto({
          dashboardId,
          favoriteName,
          organizationId,
          tabs,
          formValue: filters,
          filtersConfig,
          columnFilters,
        })
      : createFavoriteDto({
          filters: filters as IFilterOptions,
          dashboardId,
          favoriteName,
          organizationId,
          columnFilters,
        });
    return this.createFavorite(payload);
  }

  /**
   * Updates a favorite with new filter data. For multi-tab pass tabs (newFilters is flat tabId_key).
   * Pass columnFilters to persist current table column filter state.
   */
  updateFavoriteWithFilters(
    favoriteId: number,
    currentFavorite: IMatrixFavorite,
    newFilters: Record<string, unknown>,
    tabs?: { id: string }[],
    columnFilters?: IMatrixFavoriteColumnFilter[],
  ): Observable<IMatrixFavorite> {
    const normalizedFilters: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(newFilters)) {
      if (key === 'savedFilters') continue;
      if (Array.isArray(value) && value.length && typeof value[0] === 'object') {
        const flattened = this.extractTreeValues(value as any[]);
        normalizedFilters[key] = flattened.join(',');
      } else {
        normalizedFilters[key] = value;
      }
    }

    const updatedFilters = syncFavoriteFilters(
      currentFavorite.matrixFavoriteFilters || [],
      normalizedFilters,
      favoriteId,
      tabs,
    );

    const payload: IMatrixFavorite = {
      ...currentFavorite,
      matrixFavoriteFilters: updatedFilters,
      matrixFavoriteColumnFilters:
        columnFilters ?? currentFavorite.matrixFavoriteColumnFilters ?? [],
    };

    return this.updateFavorite(favoriteId, payload);
  }

  /**
   * Helper to extract string values from tree-select nodes
   */
  private extractTreeValues(nodes: any[]): string[] {
    const values: string[] = [];

    for (const node of nodes) {
      if (node?.label != null && node.label !== '') {
        values.push(String(node.label));
      } else if (node?.value) {
        values.push(node.value);
      }

      if (Array.isArray(node.children)) {
        values.push(...this.extractTreeValues(node.children));
      }
    }

    return values;
  }
}
